import React, { FC, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from 'react-query';
import { formatShortFriendlyDateWithTime } from '../../../../helpers';
import { IBuildPhoto, IResponse } from '../../../../models';
import { Table, Pagination } from '../../../../components';
import { getBuildCommunicationPhotos } from '../../../../fetch';
import { PhotoViewerModal } from './photo-viewer-modal';

interface IPhotoUploadHistoryProps {
  buildId: number;
}

export const PhotoUploadHistory: FC<IPhotoUploadHistoryProps> = ({ buildId }) => {
  const classes = useStyles();
  const [selectedPhoto, setSelectedPhoto] = useState<IBuildPhoto | null>(null);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);

  const {
    data: photosResponse,
    isLoading,
    isFetching
  } = useQuery<IResponse<IBuildPhoto[]>, Error>(
    ['getBuildCommunicationPhotos', buildId, page, perPage],
    () =>
      getBuildCommunicationPhotos(buildId, {
        sortBy: 'CreatedAt',
        sortDirection: 'Desc',
        page: page + 1,
        perPage: perPage
      }),
    {
      enabled: !!buildId,
      onSuccess: data => {
        setRecordCount(data.totalRecordCount || 0);
      }
    }
  );

  const photos = photosResponse?.records || [];

  const handleViewImage = (photo: IBuildPhoto) => {
    setSelectedPhoto(photo);
  };

  const handleCloseModal = () => {
    setSelectedPhoto(null);
  };

  const columns = [
    {
      Header: '',
      isSortable: false,
      accessor: 'createdAt',
      Cell: ({ cell: { value } }: { cell: { value: string } }) => (
        <Typography variant="body2">{formatShortFriendlyDateWithTime(value)}</Typography>
      )
    },
    {
      Header: '',
      accessor: 'createdBy',
      Cell: ({ cell: { value } }: { cell: { value: string } }) => (
        <Typography variant="body2">{value || 'Unknown'}</Typography>
      )
    },
    {
      Header: '',
      accessor: 'photoFiles',
      Cell: ({ cell: { row } }: { cell: { row: { original: IBuildPhoto } } }) => (
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            onClick={() => handleViewImage(row.original)}
            className={classes.viewIcon}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box className={classes.historyContainer}>
      <Typography variant="h6" style={{ marginBottom: photos.length > 0 ? -12 : 0 }}>
        History
      </Typography>

      {photos.length === 0 && !isLoading && !isFetching ? (
        <Typography variant="body2" color="textSecondary">
          No photos uploaded yet.
        </Typography>
      ) : (
        <>
          <Table
            columns={columns}
            data={photos}
            isLoading={isLoading || isFetching}
            hidePagination
            tableSize="small"
            rowClasses={classes.compactRow}
          />
          {!isLoading && !isFetching && (
            <Pagination
              page={page}
              count={recordCount}
              rowsPerPage={perPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </>
      )}

      <PhotoViewerModal selectedPhoto={selectedPhoto} onClose={handleCloseModal} />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  historyContainer: {
    backgroundColor: '#fff',
    padding: theme.spacing(0.25),
    height: 'fit-content',
    maxHeight: '600px',
    overflowY: 'auto'
  },
  viewIcon: {
    color: theme.palette.primary.main
  },
  compactRow: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(0.5, 1)
    }
  }
}));
